// Central catalog — ONE source of truth for nav, footer, home grid, SEO, related.

export const CATEGORIES = {
    loans: { id: "loans", label: "Loans & Mortgage", blurb: "Buy the biggest things. Pay the least for them." },
    invest: { id: "invest", label: "Invest & Retire", blurb: "Time + math = freedom, eventually." },
    debt: { id: "debt", label: "Debt Payoff", blurb: "Escape velocity from interest." },
    everyday: { id: "everyday", label: "Everyday Money", blurb: "Tip, tax, salary — the daily math." },
};

export const CALCULATORS = [
    {
        slug: "mortgage",
        title: "Mortgage Calculator",
        short: "Mortgage",
        category: "loans",
        seoTitle: "Mortgage Calculator — Monthly Payment, Amortization & Interest",
        description:
            "Estimate your monthly mortgage payment, total interest paid, and see a year-by-year amortization schedule.",
        keywords: ["mortgage calculator", "home loan payment", "amortization"],
        featured: true,
    },
    {
        slug: "loan",
        title: "Personal Loan Calculator",
        short: "Loan",
        category: "loans",
        seoTitle: "Personal Loan Calculator — Payment, Total Interest, APR",
        description: "Calculate monthly payments on any fixed-rate loan. See total cost and interest paid over the life of the loan.",
        keywords: ["loan calculator", "personal loan", "apr"],
    },
    {
        slug: "auto-loan",
        title: "Auto Loan Calculator",
        short: "Auto Loan",
        category: "loans",
        seoTitle: "Auto Loan Calculator — Car Payment with Tax, Fees, Trade-in",
        description:
            "Figure out what that car really costs every month — including sales tax, fees, and trade-in credit.",
        keywords: ["auto loan calculator", "car payment", "car affordability"],
    },
    {
        slug: "refinance",
        title: "Refinance Calculator",
        short: "Refinance",
        category: "loans",
        seoTitle: "Mortgage Refinance Calculator — Break-Even & Savings",
        description:
            "Is refinancing worth it? See your new payment, total savings, and exactly when you break even.",
        keywords: ["refinance calculator", "break even point", "mortgage refi"],
    },
    {
        slug: "compound-interest",
        title: "Compound Interest Calculator",
        short: "Compound Interest",
        category: "invest",
        seoTitle: "Compound Interest Calculator with Monthly Contributions",
        description:
            "See the eighth wonder of the world at work — model your savings growth with compounding and regular contributions.",
        keywords: ["compound interest", "investment growth"],
        featured: true,
    },
    {
        slug: "retirement",
        title: "401(k) / Retirement Calculator",
        short: "Retirement",
        category: "invest",
        seoTitle: "Retirement Calculator — 401(k) Projections & Nest Egg Math",
        description:
            "Project your retirement nest egg based on current savings, contributions, employer match, and expected returns.",
        keywords: ["retirement calculator", "401k", "nest egg"],
        featured: true,
    },
    {
        slug: "investment-roi",
        title: "Investment ROI Calculator",
        short: "ROI",
        category: "invest",
        seoTitle: "Investment ROI Calculator — Annualized Return & CAGR",
        description:
            "Calculate the true return on an investment, including annualized returns (CAGR) over any time period.",
        keywords: ["roi calculator", "cagr", "annualized return"],
    },
    {
        slug: "debt-payoff",
        title: "Debt Payoff Calculator",
        short: "Debt Payoff",
        category: "debt",
        seoTitle: "Debt Payoff Calculator — Snowball vs Avalanche",
        description:
            "Enter all your debts. See exactly how long until you're free — and which payoff strategy wins.",
        keywords: ["debt payoff", "snowball method", "avalanche method"],
        featured: true,
    },
    {
        slug: "credit-card-payoff",
        title: "Credit Card Payoff Calculator",
        short: "Credit Card",
        category: "debt",
        seoTitle: "Credit Card Payoff Calculator — Months to Zero",
        description:
            "See how many months until your credit card is paid off — and how much interest you'll pay on the way.",
        keywords: ["credit card payoff", "minimum payment", "interest"],
    },
    {
        slug: "savings-goal",
        title: "Savings Goal Calculator",
        short: "Savings Goal",
        category: "invest",
        seoTitle: "Savings Goal Calculator — How Much to Save Each Month",
        description:
            "Working toward a down payment, wedding, or big trip? See exactly how much you need to save every month.",
        keywords: ["savings calculator", "goal savings"],
    },
    {
        slug: "simple-interest",
        title: "Simple Interest Calculator",
        short: "Simple Interest",
        category: "invest",
        seoTitle: "Simple Interest Calculator — Principal × Rate × Time",
        description:
            "Classic simple interest formula — useful for short-term loans, bonds, and homework questions.",
        keywords: ["simple interest"],
    },
    {
        slug: "salary",
        title: "Salary & Hourly Calculator",
        short: "Salary",
        category: "everyday",
        seoTitle: "Salary to Hourly Calculator — Annual, Monthly, Weekly Pay",
        description:
            "Convert annual salary to hourly, weekly, bi-weekly, and monthly take-home estimates. Useful for job offers.",
        keywords: ["salary calculator", "hourly pay", "annual salary"],
    },
    {
        slug: "tip",
        title: "Tip Calculator",
        short: "Tip",
        category: "everyday",
        seoTitle: "Tip Calculator — Split the Bill with Percent, Round-up, & People",
        description:
            "Calculate tip, total, and per-person amount. Split any bill between any number of people, fast.",
        keywords: ["tip calculator", "bill splitter"],
    },
    {
        slug: "income-tax",
        title: "Income Tax Calculator",
        short: "Income Tax",
        category: "everyday",
        seoTitle: "US Federal Income Tax Calculator — Brackets & Take-Home",
        description:
            "Estimate your US federal income tax using the latest brackets. See effective rate and take-home pay.",
        keywords: ["income tax calculator", "federal tax", "take home pay"],
    },
    {
        slug: "rent-vs-buy",
        title: "Rent vs Buy Calculator",
        short: "Rent vs Buy",
        category: "loans",
        seoTitle: "Rent vs Buy Calculator — True Cost Comparison",
        description:
            "Should you rent or buy? Compare total costs over any time horizon, including home appreciation and opportunity cost.",
        keywords: ["rent vs buy", "cost comparison"],
    },
    {
        slug: "va-loan",
        title: "VA Loan Calculator",
        short: "VA Loan",
        category: "loans",
        seoTitle: "VA Loan Calculator — Zero-Down Payment & Funding Fee",
        description:
            "Estimate a VA home loan payment with the funding fee built in. Zero down, no PMI — see what eligible veterans actually pay each month.",
        keywords: ["va loan calculator", "va mortgage", "va funding fee", "veteran home loan"],
    },
    {
        slug: "fha-loan",
        title: "FHA Loan Calculator",
        short: "FHA Loan",
        category: "loans",
        seoTitle: "FHA Loan Calculator — 3.5% Down Payment with MIP",
        description:
            "Calculate an FHA mortgage payment with upfront (UFMIP) and annual mortgage insurance (MIP). Designed for 3.5%-down first-time homebuyers.",
        keywords: ["fha loan calculator", "fha mortgage", "mip", "first time home buyer"],
    },
    {
        slug: "jumbo-loan",
        title: "Jumbo Loan Calculator",
        short: "Jumbo Loan",
        category: "loans",
        seoTitle: "Jumbo Loan Calculator — Non-Conforming Mortgage Payment",
        description:
            "Estimate a jumbo mortgage payment on loans above the 2024 conforming limit of $766,550. Includes conforming-limit check.",
        keywords: ["jumbo loan calculator", "jumbo mortgage", "non conforming loan", "high balance"],
    },
    {
        slug: "heloc",
        title: "HELOC Calculator",
        short: "HELOC",
        category: "loans",
        seoTitle: "HELOC Calculator — Home Equity Line Draw & Repayment",
        description:
            "Model a Home Equity Line of Credit across draw and repayment periods. See interest-only versus full P&I payments side by side.",
        keywords: ["heloc calculator", "home equity line", "draw period", "home equity"],
    },
    {
        slug: "overtime-tips",
        title: "Overtime & Tips Tax Calculator (2025 OBBBA)",
        short: "OT & Tips Tax",
        category: "everyday",
        seoTitle: "No Tax on Overtime & Tips Calculator — 2025 OBBBA Savings",
        description:
            "See how much you'll save under the 2025 'No Tax on Overtime' and 'No Tax on Tips' deductions. Models the premium-half OT deduction, tip deduction, and MAGI phaseout.",
        keywords: ["overtime tax calculator", "no tax on tips", "no tax on overtime", "obbba 2025", "tip income deduction"],
        featured: true,
    },
    {
        slug: "student-loan",
        title: "Student Loan Calculator",
        short: "Student Loan",
        category: "loans",
        seoTitle: "Student Loan Calculator — Payoff Time, Total Interest, Refi Math",
        description:
            "Calculate your student loan monthly payment, total interest paid, and time to payoff. Model extra payments and compare standard vs. refinance scenarios.",
        keywords: ["student loan calculator", "student loan payoff", "student loan refinance", "student loan interest"],
    },
    {
        slug: "roth-vs-traditional",
        title: "Roth IRA vs Traditional 401(k) Calculator",
        short: "Roth vs 401k",
        category: "invest",
        seoTitle: "Roth IRA vs Traditional 401(k) Calculator — Which Is Better?",
        description:
            "Should you go Roth or Traditional? Compare after-tax retirement nest egg under both, factoring current vs. retirement tax rates, employer match, and decades of compounding.",
        keywords: ["roth vs traditional", "roth vs 401k", "roth ira calculator", "retirement tax comparison", "roth conversion"],
        featured: true,
    },
    {
        slug: "emergency-fund",
        title: "Emergency Fund Calculator",
        short: "Emergency Fund",
        category: "everyday",
        seoTitle: "Emergency Fund Calculator — How Many Months You Actually Need",
        description:
            "Calculate your ideal emergency fund based on monthly expenses, job stability, and household risk profile. Includes a savings timeline to reach your target.",
        keywords: ["emergency fund calculator", "how much emergency fund", "savings buffer", "rainy day fund"],
    },
];

export const getBySlug = (slug) => CALCULATORS.find((c) => c.slug === slug);
export const byCategory = () =>
    Object.values(CATEGORIES).map((cat) => ({
        ...cat,
        tools: CALCULATORS.filter((c) => c.category === cat.id),
    }));
export const relatedTo = (slug, n = 4) => {
    const c = getBySlug(slug);
    if (!c) return [];
    const same = CALCULATORS.filter((x) => x.category === c.category && x.slug !== slug);
    const others = CALCULATORS.filter((x) => x.category !== c.category);
    return [...same, ...others].slice(0, n);
};
